import "@/index.css";
import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import CrudModule from "@/components/CrudModule";
import { MODULES } from "@/lib/modules";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import CustomerDetail from "@/pages/CustomerDetail";
import InventoryPage from "@/pages/InventoryPage";
import OpeningStock from "@/pages/OpeningStock";
import CompanySettings from "@/pages/CompanySettings";
import Sales from "@/pages/Sales";
import Purchase from "@/pages/Purchase";
import PurchaseOrders from "@/pages/PurchaseOrders";
import QCTests from "@/pages/QCTests";
import QCParameters from "@/pages/QCParameters";
import Requests from "@/pages/Requests";
import RequestPermissions from "@/pages/RequestPermissions";
import NotificationCenter from "@/pages/NotificationCenter";
import Production from "@/pages/Production";
import ProductsPage from "@/pages/ProductsPage";
import Reports from "@/pages/Reports";
import UserManagement from "@/pages/UserManagement";
import AuditLog from "@/pages/AuditLog";
import DuesPage from "@/pages/DuesPage";
import BankBook from "@/pages/BankBook";
import MyHR from "@/pages/MyHR";
import EmployeeAttendance from "@/pages/EmployeeAttendance";
import PayrollRun from "@/pages/PayrollRun";
import AttendanceRegister from "@/pages/AttendanceRegister";
import ProfitLoss from "@/pages/ProfitLoss";
import GSTPage from "@/pages/GSTPage";
import DocumentsLibrary from "@/pages/DocumentsLibrary";
import PartyLedger from "@/pages/PartyLedger";
import ExpensesPage from "@/pages/ExpensesPage";
import AssetsPage from "@/pages/AssetsPage";
import JobWork from "@/pages/JobWork";
import HRDashboard from "@/pages/HRDashboard";
import BulkDailyAttendance from "@/pages/BulkDailyAttendance";
import WorkerAttendanceRegister from "@/pages/WorkerAttendanceRegister";
import ChangePassword from "@/pages/ChangePassword";
import FreightDashboard from "@/pages/seafreight/FreightDashboard";
import DispatchPage from "@/pages/DispatchPage";
import RateSearch from "@/pages/seafreight/RateSearch";
import ShippingNews from "@/pages/seafreight/ShippingNews";
import PortIntelligence from "@/pages/seafreight/PortIntelligence";
import VesselTracking from "@/pages/seafreight/VesselTracking";
import RouteExplorer from "@/pages/seafreight/RouteExplorer";
import RateAnalytics from "@/pages/seafreight/RateAnalytics";
import SavedRoutes from "@/pages/seafreight/SavedRoutes";
import FreightQuotations from "@/pages/seafreight/FreightQuotations";
import SmartAlerts from "@/pages/seafreight/SmartAlerts";
import DataSources from "@/pages/seafreight/DataSources";
import FreightSettings from "@/pages/seafreight/FreightSettings";
import { canAccess } from "@/lib/permissions";

function AccessDenied() {
  return (
    <div data-testid="access-denied" className="py-24 text-center">
      <h1 className="text-2xl font-bold text-slate-900 font-head">Access Denied</h1>
      <p className="text-slate-500 mt-2">Your role does not have permission to view this module.</p>
    </div>
  );
}

function Protected({ children, path }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#F1F5F9] text-slate-400">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password && window.location.pathname !== "/change-password") return <Navigate to="/change-password" replace />;
  if (path && !canAccess(user, path)) return <Layout><AccessDenied /></Layout>;
  return <Layout>{children}</Layout>;
}

const Module = ({ name }) => <CrudModule config={MODULES[name]} />;

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Toaster position="bottom-right" richColors />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/" element={<Protected><Dashboard /></Protected>} />
            <Route path="/customers" element={<Protected path="/customers"><Module name="customers" /></Protected>} />
            <Route path="/customers/:id" element={<Protected path="/customers"><CustomerDetail /></Protected>} />
            <Route path="/suppliers" element={<Protected path="/suppliers"><Module name="suppliers" /></Protected>} />
            <Route path="/products" element={<Protected path="/products"><ProductsPage /></Protected>} />
            <Route path="/inventory" element={<Protected path="/inventory"><InventoryPage /></Protected>} />
            <Route path="/opening-stock" element={<Protected path="/opening-stock"><OpeningStock /></Protected>} />
            <Route path="/sales" element={<Protected path="/sales"><Sales /></Protected>} />
            <Route path="/purchase" element={<Protected path="/purchase"><Purchase /></Protected>} />
            <Route path="/purchase-orders" element={<Protected path="/purchase-orders"><PurchaseOrders /></Protected>} />
            <Route path="/requests" element={<Protected path="/requests"><Requests /></Protected>} />
            <Route path="/notifications" element={<Protected path="/notifications"><NotificationCenter /></Protected>} />
            <Route path="/request-permissions" element={<Protected path="/request-permissions"><RequestPermissions /></Protected>} />
            <Route path="/production" element={<Protected path="/production"><Production /></Protected>} />
            <Route path="/dispatch" element={<Protected path="/dispatch"><DispatchPage /></Protected>} />
            <Route path="/payments" element={<Protected path="/payments"><Module name="payments" /></Protected>} />
            <Route path="/expenses" element={<Protected path="/expenses"><ExpensesPage /></Protected>} />
            <Route path="/assets" element={<Protected path="/assets"><AssetsPage /></Protected>} />
            <Route path="/job-work" element={<Protected path="/job-work"><JobWork /></Protected>} />
            <Route path="/job-work-vendors" element={<Protected path="/job-work-vendors"><Module name="job_work_vendors" /></Protected>} />
            <Route path="/ledger" element={<Protected path="/ledger"><PartyLedger /></Protected>} />
            <Route path="/ledger/:type" element={<Protected path="/ledger"><PartyLedger /></Protected>} />
            <Route path="/ledger/:type/:id" element={<Protected path="/ledger"><PartyLedger /></Protected>} />
            <Route path="/bank" element={<Protected path="/bank"><BankBook /></Protected>} />
            <Route path="/my-hr" element={<Protected path="/my-hr"><MyHR /></Protected>} />
            <Route path="/attendance/daily" element={<Protected path="/attendance/daily"><EmployeeAttendance /></Protected>} />
            <Route path="/profit-loss" element={<Protected path="/profit-loss"><ProfitLoss /></Protected>} />
            <Route path="/gst" element={<Protected path="/gst"><GSTPage /></Protected>} />
            <Route path="/sea-freight" element={<Protected path="/sea-freight"><FreightDashboard /></Protected>} />
            <Route path="/sea-freight/rates" element={<Protected path="/sea-freight/rates"><RateSearch /></Protected>} />
            <Route path="/sea-freight/news" element={<Protected path="/sea-freight/news"><ShippingNews /></Protected>} />
            <Route path="/sea-freight/ports" element={<Protected path="/sea-freight/ports"><PortIntelligence /></Protected>} />
            <Route path="/sea-freight/vessels" element={<Protected path="/sea-freight/vessels"><VesselTracking /></Protected>} />
            <Route path="/sea-freight/routes" element={<Protected path="/sea-freight/routes"><RouteExplorer /></Protected>} />
            <Route path="/sea-freight/analytics" element={<Protected path="/sea-freight/analytics"><RateAnalytics /></Protected>} />
            <Route path="/sea-freight/saved-routes" element={<Protected path="/sea-freight/saved-routes"><SavedRoutes /></Protected>} />
            <Route path="/sea-freight/quotations" element={<Protected path="/sea-freight/quotations"><FreightQuotations /></Protected>} />
            <Route path="/sea-freight/alerts" element={<Protected path="/sea-freight/alerts"><SmartAlerts /></Protected>} />
            <Route path="/sea-freight/data-sources" element={<Protected path="/sea-freight/data-sources"><DataSources /></Protected>} />
            <Route path="/sea-freight/settings" element={<Protected path="/sea-freight/settings"><FreightSettings /></Protected>} />
            <Route path="/documents" element={<Protected path="/documents"><DocumentsLibrary /></Protected>} />
            <Route path="/attendance/register" element={<Protected path="/attendance/register"><AttendanceRegister /></Protected>} />
            <Route path="/payroll-run" element={<Protected path="/payroll-run"><PayrollRun /></Protected>} />
            <Route path="/hr" element={<Protected path="/hr"><HRDashboard /></Protected>} />
            <Route path="/employees" element={<Protected path="/employees"><Module name="employees" /></Protected>} />
            <Route path="/attendance" element={<Protected path="/attendance"><Module name="attendance" /></Protected>} />
            <Route path="/leaves" element={<Protected path="/leaves"><Module name="leaves" /></Protected>} />
            <Route path="/payroll" element={<Protected path="/payroll"><Module name="payroll" /></Protected>} />
            <Route path="/daily-workers" element={<Protected path="/daily-workers"><Module name="daily_workers" /></Protected>} />
            <Route path="/daily-attendance" element={<Protected path="/daily-attendance"><Module name="daily_attendance" /></Protected>} />
            <Route path="/daily-attendance/bulk" element={<Protected path="/daily-attendance/bulk"><BulkDailyAttendance /></Protected>} />
            <Route path="/worker-attendance/register" element={<Protected path="/worker-attendance/register"><WorkerAttendanceRegister /></Protected>} />
            <Route path="/daily-wages" element={<Protected path="/daily-wages"><Module name="daily_wages" /></Protected>} />
            <Route path="/dues" element={<Protected path="/dues"><DuesPage /></Protected>} />
            <Route path="/samples" element={<Protected path="/samples"><Module name="samples" /></Protected>} />
            <Route path="/qc-tests" element={<Protected path="/qc-tests"><QCTests /></Protected>} />
            <Route path="/qc-parameters" element={<Protected path="/qc-parameters"><QCParameters /></Protected>} />
            <Route path="/company" element={<Protected path="/company"><CompanySettings /></Protected>} />
            <Route path="/reports" element={<Protected path="/reports"><Reports /></Protected>} />
            <Route path="/users" element={<Protected path="/users"><UserManagement /></Protected>} />
            <Route path="/audit" element={<Protected path="/audit"><AuditLog /></Protected>} />
            <Route path="/change-password" element={<Protected><ChangePassword /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
